import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import api from '../api';
import MoaniDisplay from './MoaniDisplay';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Collapse,
  TablePagination,
  Tabs,
  Tab
} from '@mui/material';
import {
  AccountBalance,
  SwapHoriz,
  Store,
  Token,
  Assignment,
  TrendingUp,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  DataObject
} from '@mui/icons-material';

function AssetActivity({ userId }) {
  const navigate = useNavigate();
  const { selectUserById } = useUser();


  // Get user data for display
  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    type: '',
    asset: '',
    status: '',
    counterParty: '',
    minQuantity: '',
    maxQuantity: '',
    minValue: '',
    maxValue: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Datapoints states
  const [datapointFilters, setDatapointFilters] = useState({
    name: '',
    param1: '',
    param2: ''
  });
  const [showDatapointFilters, setShowDatapointFilters] = useState(false);
  const [datapointPage, setDatapointPage] = useState(0);
  const [datapointRowsPerPage, setDatapointRowsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState(0);

  const { data: assetData, isLoading, error } = useQuery({
    queryKey: ['assetActivity', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}/asset-activity`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  // Fetch datapoints with filters and pagination
  const { data: datapointsData, isLoading: loadingDatapoints, error: datapointsError } = useQuery({
    queryKey: ['userDatapoints', userId, datapointFilters, datapointPage, datapointRowsPerPage],
    queryFn: async () => {
      if (!userId) return null;
      const params = new URLSearchParams({
        limit: datapointRowsPerPage.toString(),
        offset: (datapointPage * datapointRowsPerPage).toString(),
        ...Object.fromEntries(Object.entries(datapointFilters).filter(([_, v]) => v))
      });
      const response = await api.get(`/users/${userId}/datapoints?${params}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 30000
  });

  // Extract data safely (always runs regardless of loading state)
  const { overview = {}, transactions = [] } = assetData || {};

  // Get unique values for filter dropdowns
  const uniqueValues = useMemo(() => {
    if (!transactions.length) return { types: [], assets: [], statuses: [] };
    
    const types = [...new Set(transactions.map(t => t.type).filter(Boolean))];
    const assets = [...new Set(transactions.map(t => t.assetTitle).filter(Boolean))];
    const statuses = [...new Set(transactions.map(t => t.status).filter(Boolean))];
    
    return { types, assets, statuses };
  }, [transactions]);

  // Filter transactions based on current filters
  const filteredTransactions = useMemo(() => {
    if (!transactions.length) return [];
    
    return transactions.filter(transaction => {
      // Date filters
      if (filters.dateFrom) {
        const transactionDate = new Date(transaction.createdAt);
        const fromDate = new Date(filters.dateFrom);
        if (transactionDate < fromDate) return false;
      }
      
      if (filters.dateTo) {
        const transactionDate = new Date(transaction.createdAt);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (transactionDate > toDate) return false;
      }
      
      // Type filter
      if (filters.type && !transaction.type?.toLowerCase().includes(filters.type.toLowerCase())) {
        return false;
      }
      
      // Asset filter
      if (filters.asset && !transaction.assetTitle?.toLowerCase().includes(filters.asset.toLowerCase())) {
        return false;
      }
      
      // Status filter
      if (filters.status && transaction.status?.toLowerCase() !== filters.status.toLowerCase()) {
        return false;
      }
      
      // Counter Party filter
      if (filters.counterParty && !transaction.counterPartyName?.toLowerCase().includes(filters.counterParty.toLowerCase())) {
        return false;
      }
      
      // Quantity filters
      const quantity = transaction.quantity || transaction.stackSize || 1;
      if (filters.minQuantity && quantity < Number(filters.minQuantity)) {
        return false;
      }
      if (filters.maxQuantity && quantity > Number(filters.maxQuantity)) {
        return false;
      }
      
      // Value filters
      if (transaction.value) {
        const value = Number(transaction.value) / 1e18; // Convert from wei to MOANI
        if (filters.minValue && value < Number(filters.minValue)) {
          return false;
        }
        if (filters.maxValue && value > Number(filters.maxValue)) {
          return false;
        }
      }
      
      return true;
    });
  }, [transactions, filters]);

  // Early returns after all hooks have been called
  if (!userId) {
    return (
      <Box display="flex" justifyContent="center" mt={4} sx={{ p: 2 }}>
        <Typography variant="h6">No user ID provided.</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>Loading asset activity...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load asset activity: {error.message}
      </Alert>
    );
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      type: '',
      asset: '',
      status: '',
      counterParty: '',
      minQuantity: '',
      maxQuantity: '',
      minValue: '',
      maxValue: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Datapoint filter handlers
  const handleDatapointFilterChange = (field, value) => {
    setDatapointFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setDatapointPage(0); // Reset to first page when filters change
  };

  const clearDatapointFilters = () => {
    setDatapointFilters({
      name: '',
      param1: '',
      param2: ''
    });
    setDatapointPage(0);
  };

  const hasActiveDatapointFilters = Object.values(datapointFilters).some(value => value !== '');

  const handleDatapointPageChange = (event, newPage) => {
    setDatapointPage(newPage);
  };

  const handleDatapointRowsPerPageChange = (event) => {
    setDatapointRowsPerPage(parseInt(event.target.value, 10));
    setDatapointPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatMoani = (amount) => {
    if (!amount) return '0.00';
    return (Number(amount) / 1e18).toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTransactionIcon = (type) => {
    const typeStr = type?.toLowerCase() || '';
    if (typeStr.includes('auction') || typeStr.includes('sale') || typeStr.includes('purchase')) {
      return <Store />;
    } else if (typeStr.includes('mint')) {
      return <Token />;
    } else if (typeStr.includes('inventory') || typeStr.includes('transfer')) {
      return <SwapHoriz />;
    } else if (typeStr.includes('reward')) {
      return <TrendingUp />;
    } else {
      return <Assignment />;
    }
  };

  const getTransactionColor = (type) => {
    const typeStr = type?.toLowerCase() || '';
    if (typeStr.includes('sale') || typeStr.includes('received') || typeStr.includes('reward')) {
      return 'success';
    } else if (typeStr.includes('purchase') || typeStr.includes('sent') || typeStr.includes('spent')) {
      return 'error';
    } else if (typeStr.includes('mint')) {
      return 'warning';
    } else if (typeStr.includes('auction')) {
      return 'primary';
    } else {
      return 'info';
    }
  };


  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Asset Activity - {userData?.name || 'User'}
      </Typography>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Store sx={{ fontSize: 40, color: '#4caf50' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Auctions
                  </Typography>
                  <Typography variant="h6" color="white">
                    {overview.totalAuctions || 0}
                  </Typography>
                  <MoaniDisplay 
                    amount={Number(overview.totalAuctionValue) / 1e18} 
                    isWei={false}
                    variant="body2" 
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Token sx={{ fontSize: 40, color: '#ff9800' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Mint Requests
                  </Typography>
                  <Typography variant="h6" color="white">
                    {overview.totalMintRequests || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    {overview.pendingMintRequests || 0} pending
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <SwapHoriz sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Inventory Items
                  </Typography>
                  <Typography variant="h6" color="white">
                    {overview.totalInventoryItems || 0}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    {overview.uniqueAssets || 0} unique assets
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AccountBalance sx={{ fontSize: 40, color: '#fff' }} />
                <Box>
                  <Typography color="text.secondary" variant="subtitle2">
                    Asset Value
                  </Typography>
                  <MoaniDisplay 
                    amount={Number(overview.totalAssetValue) / 1e18} 
                    isWei={false}
                    variant="h6" 
                    sx={{ color: 'white' }}
                  />
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Estimated total
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transaction History */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Asset Transaction History
            {filteredTransactions.length !== transactions.length && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({filteredTransactions.length} of {transactions.length} transactions)
              </Typography>
            )}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {hasActiveFilters && (
              <Button
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
                color="secondary"
              >
                Clear Filters
              </Button>
            )}
            <Button
              startIcon={<FilterList />}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowFilters(!showFilters)}
              variant={hasActiveFilters ? "contained" : "outlined"}
              size="small"
            >
              Filters
            </Button>
          </Stack>
        </Stack>

        <Collapse in={showFilters}>
          <Paper variant="outlined" sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Grid container spacing={2}>
              {/* Date Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Date From"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Date To"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Type Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {uniqueValues.types.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Asset Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Asset</InputLabel>
                  <Select
                    value={filters.asset}
                    onChange={(e) => handleFilterChange('asset', e.target.value)}
                    label="Asset"
                  >
                    <MenuItem value="">All Assets</MenuItem>
                    {uniqueValues.assets.map(asset => (
                      <MenuItem key={asset} value={asset}>{asset}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {uniqueValues.statuses.map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Counter Party Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Counter Party"
                  value={filters.counterParty}
                  onChange={(e) => handleFilterChange('counterParty', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Search by name..."
                />
              </Grid>

              {/* Quantity Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Quantity"
                  type="number"
                  value={filters.minQuantity}
                  onChange={(e) => handleFilterChange('minQuantity', e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Max Quantity"
                  type="number"
                  value={filters.maxQuantity}
                  onChange={(e) => handleFilterChange('maxQuantity', e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              {/* Value Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Value (MOANI)"
                  type="number"
                  value={filters.minValue}
                  onChange={(e) => handleFilterChange('minValue', e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Max Value (MOANI)"
                  type="number"
                  value={filters.maxValue}
                  onChange={(e) => handleFilterChange('maxValue', e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        <Divider sx={{ mb: 2 }} />
        
        {transactions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No asset transactions found
          </Typography>
        ) : filteredTransactions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No transactions match the current filters
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Asset</strong></TableCell>
                  <TableCell><strong>Quantity</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Counter Party</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={`${transaction.source}-${transaction.id}`}>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transaction.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getTransactionIcon(transaction.type)}
                        label={transaction.type}
                        color={getTransactionColor(transaction.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.assetTitle || 'Unknown Asset'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {transaction.assetId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.quantity || transaction.stackSize || 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.value ? (
                        <MoaniDisplay 
                          amount={Number(transaction.value) / 1e18} 
                          isWei={false}
                          variant="body2" 
                          sx={{ fontFamily: 'monospace' }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.status || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        component={transaction.counterPartyId ? 'a' : 'span'}
                        href={transaction.counterPartyId ? `/user/${transaction.counterPartyId}/details` : undefined}
                        variant="body2"
                        sx={{ 
                          cursor: transaction.counterPartyId ? 'pointer' : 'default',
                          color: transaction.counterPartyId ? 'primary.main' : 'inherit',
                          textDecoration: transaction.counterPartyId ? 'underline' : 'none',
                          '&:hover': transaction.counterPartyId ? {
                            color: 'primary.dark',
                            textDecoration: 'underline'
                          } : {}
                        }}
                        onClick={(event) => transaction.counterPartyId && selectUserById(transaction.counterPartyId, navigate)}
                        title={transaction.counterPartyId ? 'Click to view this user (Ctrl+click for new tab)' : ''}
                      >
                        {transaction.counterPartyName || 'N/A'}
                      </Typography>
                      {transaction.counterPartyWallet && (
                        <Typography variant="caption" color="text.secondary">
                          {`${transaction.counterPartyWallet.slice(0, 6)}...${transaction.counterPartyWallet.slice(-4)}`}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Tabs for Asset Activity and Datapoints */}
      <Paper sx={{ mt: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Asset Transactions" />
          <Tab label="Datapoints" icon={<DataObject />} iconPosition="start" />
        </Tabs>

        {/* Datapoints Section */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">
                User Datapoints
                {datapointsData && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({datapointsData.totalCount} total datapoints)
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                {hasActiveDatapointFilters && (
                  <Button
                    startIcon={<Clear />}
                    onClick={clearDatapointFilters}
                    size="small"
                    color="secondary"
                  >
                    Clear Filters
                  </Button>
                )}
                <Button
                  startIcon={<FilterList />}
                  endIcon={showDatapointFilters ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setShowDatapointFilters(!showDatapointFilters)}
                  variant={hasActiveDatapointFilters ? "contained" : "outlined"}
                  size="small"
                >
                  Filters
                </Button>
              </Stack>
            </Stack>

            <Collapse in={showDatapointFilters}>
              <Paper variant="outlined" sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Name"
                      value={datapointFilters.name}
                      onChange={(e) => handleDatapointFilterChange('name', e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Search by datapoint name..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Param1"
                      value={datapointFilters.param1}
                      onChange={(e) => handleDatapointFilterChange('param1', e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Search by param1..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Param2"
                      value={datapointFilters.param2}
                      onChange={(e) => handleDatapointFilterChange('param2', e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="Search by param2..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Collapse>

            <Divider sx={{ mb: 2 }} />

            {loadingDatapoints ? (
              <Box display="flex" justifyContent="center" mt={4}>
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress />
                  <Typography>Loading datapoints...</Typography>
                </Stack>
              </Box>
            ) : datapointsError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to load datapoints: {datapointsError.message}
              </Alert>
            ) : !datapointsData?.dataPoints?.length ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No datapoints found {hasActiveDatapointFilters ? 'matching the current filters' : 'for this user'}
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>ID</strong></TableCell>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell><strong>Param1</strong></TableCell>
                        <TableCell><strong>Param2</strong></TableCell>
                        <TableCell><strong>Value</strong></TableCell>
                        <TableCell><strong>Created</strong></TableCell>
                        <TableCell><strong>Updated</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {datapointsData.dataPoints.map((datapoint) => (
                        <TableRow key={datapoint.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {datapoint.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {datapoint.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {datapoint.param1 || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {datapoint.param2 || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {datapoint.value || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(datapoint.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {datapoint.updatedAt ? formatDate(datapoint.updatedAt) : 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={datapointsData.totalCount}
                  page={datapointPage}
                  onPageChange={handleDatapointPageChange}
                  rowsPerPage={datapointRowsPerPage}
                  onRowsPerPageChange={handleDatapointRowsPerPageChange}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default AssetActivity; 